import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChefHat, Loader2, Eye, EyeOff, Lock, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setValidating(false);
                setTokenValid(false);
                return;
            }
            
            try {
                const response = await api.verifyResetToken(token);
                if (response.data.valid) {
                    setTokenValid(true);
                } else {
                    setTokenValid(false);
                }
            } catch (error) {
                console.error('Token validation error:', error);
                setTokenValid(false);
            } finally {
                setValidating(false);
            }
        };
        
        validateToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!password || !confirmPassword) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }
        
        if (password.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caractères');
            return;
        }
        
        if (password !== confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        
        setLoading(true);
        try {
            await api.resetPassword(token, password);
            setSuccess(true);
            toast.success('Mot de passe réinitialisé avec succès!');
        } catch (error) {
            console.error('Reset password error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
        } finally {
            setLoading(false);
        }
    };

    // Loading state while validating token
    if (validating) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-muted-foreground">Validation du lien...</p>
                </div>
            </div>
        );
    }

    // Invalid or expired token
    if (!tokenValid && !success) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
                <div className="blob-green top-10 -left-20 opacity-30" />
                <div className="blob-purple bottom-10 -right-20 opacity-30" />
                
                <div className="w-full max-w-md relative z-10">
                    <Link to="/" className="flex items-center justify-center gap-3 mb-8 group">
                        <div className="p-3 bg-gradient-cameleon rounded-2xl border-2 border-foreground shadow-pop group-hover:shadow-pop-hover transition-all">
                            <ChefHat className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <span className="font-heading font-black text-2xl block">Recettes</span>
                            <span className="font-heading font-black text-2xl text-gradient-cameleon -mt-1 block">Caméléon</span>
                        </div>
                    </Link>
                    
                    <div className="bg-white rounded-3xl border-2 border-foreground shadow-[8px_8px_0px_0px_#1e293b] p-8 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-red-500">
                            <XCircle className="w-8 h-8 text-red-600" />
                        </div>
                        <h1 className="font-heading text-2xl font-black text-foreground mb-2">Lien invalide ou expiré</h1>
                        <p className="text-muted-foreground mb-6">
                            Ce lien de réinitialisation n'est plus valide. Les liens expirent après 1 heure pour des raisons de sécurité.
                        </p>
                        <Link to="/forgot-password">
                            <Button className="btn-pop bg-primary text-white" data-testid="request-new-link-btn">
                                Demander un nouveau lien
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
            {/* Decorative blobs */}
            <div className="blob-green top-10 -left-20 opacity-30" />
            <div className="blob-purple bottom-10 -right-20 opacity-30" />
            <div className="blob-orange top-1/2 right-10 opacity-20" />
            
            <div className="w-full max-w-md relative z-10">
                {/* Logo */}
                <Link to="/" className="flex items-center justify-center gap-3 mb-8 group">
                    <div className="p-3 bg-gradient-cameleon rounded-2xl border-2 border-foreground shadow-pop group-hover:shadow-pop-hover transition-all">
                        <ChefHat className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <span className="font-heading font-black text-2xl block">Recettes</span>
                        <span className="font-heading font-black text-2xl text-gradient-cameleon -mt-1 block">Caméléon</span>
                    </div>
                </Link>
                
                {/* Card */}
                <div className="bg-white rounded-3xl border-2 border-foreground shadow-[8px_8px_0px_0px_#1e293b] p-8">
                    {!success ? (
                        <>
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-foreground">
                                    <Lock className="w-8 h-8 text-purple-600" />
                                </div>
                                <h1 className="font-heading text-3xl font-black text-foreground mb-2">Nouveau mot de passe</h1>
                                <p className="text-muted-foreground">
                                    Choisissez un nouveau mot de passe sécurisé pour votre compte.
                                </p>
                            </div>
                            
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="font-bold">Nouveau mot de passe</Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="rounded-xl border-2 border-foreground py-6 pr-12 focus:shadow-pop transition-shadow"
                                            data-testid="reset-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                    <p className="text-xs text-muted-foreground">Minimum 6 caractères</p>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword" className="font-bold">Confirmer le mot de passe</Label>
                                    <div className="relative">
                                        <Input
                                            id="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="rounded-xl border-2 border-foreground py-6 pr-12 focus:shadow-pop transition-shadow"
                                            data-testid="reset-password-confirm-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>
                                
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full btn-pop bg-primary text-white py-6 text-lg"
                                    data-testid="reset-password-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Réinitialisation...
                                        </>
                                    ) : (
                                        <>
                                            <Lock className="w-5 h-5 mr-2" />
                                            Réinitialiser le mot de passe
                                        </>
                                    )}
                                </Button>
                            </form>
                        </>
                    ) : (
                        <div className="text-center py-4">
                            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-green-500">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                            <h2 className="font-heading text-2xl font-black text-foreground mb-2">Mot de passe réinitialisé!</h2>
                            <p className="text-muted-foreground mb-6">
                                Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
                            </p>
                            <Button 
                                onClick={() => navigate('/login')}
                                className="btn-pop bg-primary text-white"
                                data-testid="go-to-login-btn"
                            >
                                Se connecter
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
